import React, { type SyntheticEvent, useCallback } from 'react';

import { type UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { type TriggerProps } from '@atlaskit/popup';
import { ModalContextProvider } from '@atlassian/entry-point-modal-context/ModalContextProvider';
import { ModalTrigger } from '@atlassian/entry-point-modal-trigger';
import { PopupTrigger } from '@atlassian/entry-point-popup';
import { usePeopleTeamAnalyticsEvents } from '@atlassian/people-teams-ui/analytics';

import PopupComponent from '../../../../common/no-background-popup-component';
import { getSiteId } from '../../../../common/utils/ari';
import { isTeamBiDirectionalEnabled } from '../../../../common/utils/utils';
import { asyncAddTeamEntryPoint } from '../../../team-selector/add-team-modal';
import { asyncTeamSelectorEntryPoint } from '../../../team-selector/entrypoint';
import TeamSelectorSkeleton from '../../../team-selector/team-selector-skeleton';
import { TeamButtonTrigger } from '../team-button-trigger';

type TeamButtonComponentProps = {
	containerId: string;
	orgId?: string;
	Wrapper?: React.ElementType;
	numberOfTeams?: number;
};

export const TeamButtonComponent = ({
	numberOfTeams,
	containerId,
	orgId,
	Wrapper = React.Fragment,
}: TeamButtonComponentProps) => {
	const { fireEvent } = usePeopleTeamAnalyticsEvents();

	const onClick = useCallback(
		(_: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
			const result = analyticsEvent?.context.find(
				(item) => item.attributes && item.attributes.scope !== undefined,
			);
			fireEvent('ui.teamButton.clicked', {
				teamsCount: numberOfTeams ?? 0,
				scope: result?.attributes?.scope,
			});
		},
		[fireEvent, numberOfTeams],
	);

	if (numberOfTeams === undefined) {
		return null;
	}

	if (numberOfTeams === 0) {
		const isExperimentEnabled = isTeamBiDirectionalEnabled(true);
		const siteId = containerId && getSiteId(containerId);

		if (isExperimentEnabled && siteId && orgId) {
			return (
				<ModalContextProvider>
					<ModalTrigger
						entryPoint={asyncAddTeamEntryPoint}
						entryPointProps={{
							onTeamAdd: () => {
								// TODO: Handle team addition via the createTeamConnectedToContainer mutation
								return;
							},
							siteId,
							orgId,
						}}
						entryPointParams={{ containerId: containerId }}
						modalProps={{ width: 'small' }}
					>
						{({ ref }) => (
							<Wrapper>
								<TeamButtonTrigger
									triggerProps={{ ref }}
									numberOfTeams={numberOfTeams}
									onClick={onClick}
								/>
							</Wrapper>
						)}
					</ModalTrigger>
				</ModalContextProvider>
			);
		}

		return null;
	}

	return (
		<PopupTrigger
			entryPoint={asyncTeamSelectorEntryPoint}
			entryPointParams={{ containerId: containerId }}
			entryPointProps={{ containerId: containerId }}
			trigger={(triggerProps: TriggerProps) => (
				<Wrapper>
					<TeamButtonTrigger
						triggerProps={triggerProps}
						numberOfTeams={numberOfTeams}
						onClick={onClick}
					/>
				</Wrapper>
			)}
			fallback={<TeamSelectorSkeleton />}
			placement="bottom-start"
			strategy="absolute"
			popupComponent={PopupComponent}
		/>
	);
};
